// src/components/HowTo.jsx
import React from "react";

function HowTo() {
  return (
    <div style={{ padding: "1rem", maxWidth: "800px", margin: "0 auto" }}>
      {/* Only this H1 uses .howto-title to center it */}
      <h1 className="howto-title">How to Use the Feedback Page</h1>

      <p>
        This guide walks you through the Feedback page features step by step. It’s 
        written for new users who need a basic understanding of how to create, 
        manage, and analyze feedback groups.
      </p>

      <h2>1. Overview of the Feedback Page</h2>
      <p>
        When you first arrive on the Feedback page, you’ll see two main sections:
      </p>
      <ol>
        <li><strong>Create (or Edit) Feedback Group</strong> section at the top.</li>
        <li><strong>Saved Feedback Groups</strong> section below, listing all your existing groups.</li>
      </ol>
      <p>
        You can use this page to:
      </p>
      <ul>
        <li>Create a new group by entering a title and a topic (question or description).</li>
        <li>Generate a sharing link so others can respond.</li>
        <li>Track how many people have responded so far.</li>
        <li>Close the session when you’re done collecting responses.</li>
        <li>Request and view an AI-powered analysis of your results.</li>
      </ul>

      <h2>2. Creating a New Feedback Group</h2>
      <ol>
        <li><strong>Find “Create a New Feedback Group”:</strong> 
          <p>
            At the top of the page, look for the heading that says <em>“Create a New Feedback Group.”</em>
          </p>
        </li>
        <li><strong>Enter Title & Topic:</strong>
          <ul>
            <li>
              <strong>Title</strong> is a short descriptive name (e.g., “Customer Satisfaction Feedback) 
              with a character limit (75). 
            </li>
            <li>
              <strong>Topic</strong> is where you put your main question or instructions. 
              There’s a character limit of 2000.
            </li>
          </ul>
        </li>
        <li><strong>Save the Feedback Group:</strong>
          <p>
            Click the <em>“Save Feedback Group</em> button. Once saved, your new group will 
            appear under <strong>“Saved Feedback Groups.”</strong>
          </p>
          <p>
            <em>Tip:</em> If you want to edit an existing group, scroll down to “Saved Feedback Groups” 
            and click the Edit button (the pencil icon). That will bring you back up here with 
            the existing details ready to edit.
          </p>
        </li>
      </ol>

      <h2>3. Viewing and Managing Your Saved Feedback Groups</h2>
      <p>
        Below the creation form, you’ll see a heading “Saved Feedback Groups.” This section shows each 
        of your groups in a “card” format.
      </p>

      <h3>3.1 Expanding or Minimizing Group Cards</h3>
      <p>
        Each group card has a “+” or “-” button in the top-right corner, which lets you expand 
        or collapse its details. Use this to keep your view organized if you have many groups.
      </p>

      <h3>3.2 Reading Group Details</h3>
      <ul>
        <li><strong>Title & Topic:</strong> Click on these texts to see the full content in a pop-up if it’s truncated (helpful on mobile).</li>
        <li><strong>Created Date:</strong> Shows when the group was first saved.</li>
        <li><strong>Response Link:</strong> Once generated, this is how respondents access your group. Click the copy icon to copy it to your clipboard. Send this to your recipeints with a brief explanation of how this works.</li>
      </ul>

      <h3>3.3 Editing a Feedback Group Draft</h3>
      <p>
        If a group <em>hasn’t</em> had a link generated yet, you can:
      </p>
      <ul>
        <li>Click the <strong>Edit</strong> button (pencil icon).</li>
        <li>This takes you back up to the form, pre-filled with the group's existing information.</li>
        <li>Make any changes and click <em>“Save Changes.”</em></li>
      </ul>
      <p>
        <em>Important:</em> Once a link is generated, editing is locked.
      </p>

      <h3>3.4 Deleting a Feedback Group Draft</h3>
      <p>
        To delete:
      </p>
      <ul>
        <li>Click the <strong>Delete</strong> button (trash icon).</li>
        <li>Confirm the deletion when prompted.</li>
        <li>Groups with a Generated Link cannot be deleted.</li>
      </ul>
      <p>
        This action is permanent, so be sure you no longer need the group before deleting.
      </p>

      <h2>4. Generating a Link for Respondents</h2>
      <p>
        A response link must be generated so people can respond. Here’s how:
      </p>
      <ol>
        <li><strong>Check if the Feedback Group is Free or Paid:</strong> 
          <p>
            Free feedback groups allow a fixed number of responses (e.g., 25). Paid feedback groups let 
            you enter a custom range (e.g., 10–1000 responses).
          </p>
        </li>
        <li><strong>Generate the Link:</strong>
          <ul>
            <li>
              <em>Free Feedback Group:</em> Click <strong>“Free Generate Link”</strong> (with the default 
              number of responses).
            </li>
            <li>
              <em>Paid Feedback Group:</em> Specify the number of responses and click 
              <strong> “Generate Link.”</strong> If required, complete the payment process.
            </li>
          </ul>
        </li>
        <li><strong>Copy and Share:</strong>
          <p>
            After generating, copy the link and send it via email, social media, or however 
            you plan to reach participants. Including a brief explanation is recommended.
          </p>
        </li>
      </ol>
      <p>
        <em>Note:</em> Once a link is generated, you can’t edit the group's title or topic.
      </p>

      <h2>5. Tracking Responses and Closing the Feedback Group</h2>
      <h3>5.1 Viewing Response Data</h3>
      <ul>
        <li><strong>Response Limit:</strong> Shows the max number of people who can submit responses.</li>
        <li><strong>Responses to Date:</strong> Shows how many have already responded.</li>
      </ul>

      <h3>5.2 Days Open</h3>
      <p>
        If a link was generated, you’ll see how many days it’s been since the link was created. 
        This helps track how long the group has been live.
      </p>

      <h3>5.3 Closing the Feedback Group</h3>
      <p>
        Once you have enough responses or want to stop collecting them:
      </p>
      <ul>
        <li>Click <em>“Close Session.”</em></li>
        <li>Confirm you really want to close it.</li>
        <li>After closing, no new responses can be submitted.</li>
      </ul>

      <h2>6. AI Analysis of Your Feedback Group</h2>
      <p>
        After closing a session, you can request or view AI Analysis if you want summarized feedback. 
        This includes:
      </p>
      <ol>
        <li><strong>Request AI Analysis:</strong> If you haven’t yet, click <em>“AI Analysis.”</em> 
          Confirm and wait while the system processes your group responses.
        </li>
        <li><strong>Wait for Completion:</strong> Analysis can take a few minutes. You can refresh the 
          page later to check if it’s done.
        </li>
        <li><strong>View Analysis:</strong> Once complete, click <em>“View Analysis.”</em> to see 
          summarized bullet points or any other insights.
        </li>
      </ol>

      <h2>7. Viewing Full Text Fields</h2>
      <p>
        If your Title, Topic, or other text is too long and truncated (especially on mobile), click it to open a pop-up 
        window displaying the full content.
      </p>

      <h2>8. Common Questions</h2>
      <p>
        <strong>1. Why can’t I edit a group after generating a link?</strong><br />
        Editing is locked once a link is generated to keep the feedback group consistent for respondents.
      </p>
      <p>
        <strong>2. Why do I see an “Analysis in Progress” overlay?</strong><br />
        The AI is currently analyzing your responses. Feel free to do other tasks while it completes.
      </p>
      <p>
        <strong>3. Why must I be logged in?</strong><br />
        An authentication token is needed for saving groups, generating links, or requesting analysis. 
        Make sure you’re logged in.
      </p>

      <h2>9. Summary of Key Actions</h2>
      <ul>
        <li><strong>Create a Feedback Group:</strong> Add Title + Topic → “Save Feedback Group.”</li>
        <li><strong>Edit/Delete:</strong> Scroll to “Saved Feedback Groups" → Click “Edit” or “Delete.”</li>
        <li><strong>Generate Link:</strong> Set responses → “Generate Link” → Copy & share.</li>
        <li><strong>Close Session:</strong> Stop responses by clicking “Close Session.”</li>
        <li><strong>Request/View AI Analysis:</strong> Once closed, see summarized insights.</li>
      </ul>
    </div>
  );
}

export default HowTo;
